/**
 * VianBills — Navigation data
 *
 * Single source of truth for sidebar groups and breadcrumb map.
 * Imported by Layout.jsx (desktop sidebar) and MobileDrawer.jsx (mobile drawer).
 * Do NOT define navigation structure anywhere else.
 */

import {
  LayoutDashboard, Users, Package, FileText, Receipt, FileCheck2,
  Landmark, Hash, ShieldCheck, DatabaseZap, ClipboardList, ClipboardCheck,
  Truck, ShoppingCart, FileMinus, FilePlus2, Building2, ClipboardEdit,
  Activity, Split, Wallet, TrendingDown, BarChart3, Factory,
  Scale, Layers, FileCode2, LineChart, Mail, Trash2, FileWarning, FlaskConical, GitMerge, Ruler,
  AlertTriangle, Target, CalendarClock, Calendar, Navigation, ShieldAlert, MessageCircle, Megaphone,
} from "lucide-react";

export const MANUFACTURING_TIME_PERMISSIONS = [
  "work_orders", "preparation_floor", "production_floor", "qc_floor", "packing_floor", "dispatch_floor",
].map(module => [module, "view"]);

// Keep the no-tab sidebar selection consistent with the Hub's first permitted floor.
export function defaultManufacturingTab(can) {
  return [
    ["preparation", "preparation_floor"], ["production", "production_floor"],
    ["qc", "qc_floor"], ["billing", "invoices"],
    ["packing", "packing_floor"], ["dispatch", "dispatch_floor"],
  ].find(([, module]) => can(module, "view"))?.[0] || "";
}

export function navigationItemIsActive(item, location, defaultTab = "") {
  const [path, search = ""] = item.to.split("?");
  if (location.pathname !== path &&
      (path === "/" || item.end || !location.pathname.startsWith(`${path}/`))) return false;
  const current = new URLSearchParams(location.search);
  return [...new URLSearchParams(search)].every(([key, value]) => {
    const selected = current.get(key) || (path === "/manufacturing" && key === "tab" ? defaultTab : "");
    return selected === value;
  });
}

export function routePermissionAllowed(
  { module, action, any, adminOnly, financeOnly }, can, roleCodes = [], financeRestricted = false,
) {
  if (financeOnly && financeRestricted) return false;
  if (adminOnly && !module) return can("settings", action || "view");
  return any ? any.some(([mod, act]) => can(mod, act)) : can(module, action || "view");
}

/* -----------------------------------------------------------------------
   Grouped sidebar navigation.
   Each item declares which permission module it belongs to (`mod`).
   Any user with ANY action on that module sees the entry.
   Groups auto-hide when they contain zero visible items.
   -------------------------------------------------------------------- */
export const GROUPS = [
  {
    label: null,     // ungrouped — always at top
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", mod: "dashboard" },
      { to: "/chat", label: "Chat", icon: MessageCircle, testid: "nav-chat" },
    ],
  },
  {
    label: "Sales & CRM",
    items: [
      { to: "/customers",         label: "Customers",          icon: Users,         testid: "nav-customers",         mod: "customers" },
      { to: "/customer-pos",      label: "Customer POs",       icon: ClipboardEdit, testid: "nav-customer-pos",      mod: "customers" },
      { to: "/quotations",        label: "Quotations",         icon: FileText,      testid: "nav-quotations",        mod: "quotations" },
      { to: "/proformas",         label: "Proforma Invoices",  icon: FileCheck2,    testid: "nav-proformas",         mod: "proformas" },
      { to: "/invoices",          label: "Sales Invoices",     icon: Receipt,       testid: "nav-invoices",          mod: "invoices" },
      { to: "/delivery-challans", label: "Delivery Challans",  icon: Truck,         testid: "nav-delivery-challans", mod: "delivery_challans" },
      { to: "/receipts",          label: "Payment Receipts",   icon: Wallet,        testid: "nav-receipts",          mod: "receipts" },
    ],
  },
  {
    label: "Sales & Marketing",
    items: [
      { to: "/sm/dashboard", label: "Dashboard", icon: BarChart3,  testid: "nav-sm-dashboard", mod: "sales_marketing" },
      { to: "/sm/today",     label: "Today",     icon: Calendar,   testid: "nav-sm-today",     mod: "sales_marketing" },
      { to: "/sm/leads",     label: "Leads",     icon: Target,     testid: "nav-sm-leads",     mod: "sales_marketing" },
      { to: "/sm/routes",    label: "Routes",    icon: Navigation, testid: "nav-sm-routes",    mod: "sales_marketing" },
    ],
  },
  {
    label: "Manufacturing",
    items: [
      { to: "/work-orders",                     label: "Work Orders",      icon: ClipboardList,  testid: "nav-work-orders",        mod: "work_orders" },
      { to: "/production-planning",             label: "Production Planning", icon: CalendarClock, testid: "nav-production-planning", anyPerms: [["production_planning","view"],["production_planning","manage"]] },
      { to: "/manufacturing?tab=preparation",   label: "Preparation Floor",icon: Factory,        testid: "nav-preparation-floor",  mod: "preparation_floor" },
      { to: "/manufacturing?tab=production",    label: "Assembly Floor",   icon: Factory,        testid: "nav-production-floor",   mod: "production_floor" },
      { to: "/manufacturing?tab=qc",            label: "Quality Control",  icon: ClipboardCheck, testid: "nav-qc-floor",           mod: "qc_floor" },
      { to: "/manufacturing?tab=packing",       label: "Packing",          icon: Package,        testid: "nav-packing-floor",      mod: "packing_floor" },
      { to: "/manufacturing?tab=dispatch",      label: "Dispatch",         icon: Truck,          testid: "nav-dispatch-floor",     mod: "dispatch_floor" },
      { to: "/production-ncs",                  label: "Production NCs",   icon: ShieldAlert,    testid: "nav-production-ncs",     mod: "production_nc" },
    ],
  },
  {
    label: "Inventory",
    items: [
      { to: "/templates", label: "Product Templates", icon: Package, testid: "nav-templates", mod: "product_templates" },
      { to: "/hsn",       label: "HSN Codes",  icon: Hash,     testid: "nav-hsn",       mod: "hsn_codes" },
      { to: "/units",     label: "Units",       icon: Ruler,    testid: "nav-units",     adminOnly: true },
    ],
  },
  {
    label: "Store",
    items: [
      { to: "/store/materials", label: "Store Materials", icon: FlaskConical, testid: "nav-store-materials", mod: "inventory_movements", action: "view" },
      { to: "/store/receiving", label: "Purchase Orders to Receive", icon: ShoppingCart, testid: "nav-store-purchase-orders", mod: "material_receipts", anyActions: ["view", "create", "inspect"] },
      { to: "/material-receipts", label: "Material Receipts", icon: ClipboardCheck, testid: "nav-material-receipts", mod: "material_receipts", anyPerms: [["material_receipts","view"],["material_receipts","create"],["material_receipts","inspect"],["material_receipts","close"],["supplier_invoices","create"]] },
      { to: "/store/material-issues", label: "Material Issue & Return", icon: ClipboardEdit, testid: "nav-store-material-issues", mod: "inventory_movements", action: "view" },
    ],
  },
  {
    label: "Purchase",
    items: [
      { to: "/vendors",                  label: "Vendors",                  icon: Building2,     testid: "nav-vendors",                  mod: "vendors" },
      { to: "/raw-materials",            label: "Purchase Raw Materials",    icon: FlaskConical,  testid: "nav-raw-materials",            mod: "raw_materials" },
      { to: "/vendor-material-mappings", label: "Vendor Mappings",          icon: GitMerge,      testid: "nav-vmm",                      mod: "vendor_material_mapping" },
      { to: "/purchase-orders",          label: "Purchase Orders",          icon: ShoppingCart,  testid: "nav-purchase-orders",          mod: "purchase_orders", action: "view" },
       { to: "/purchase-requisitions",    label: "Purchase Requisitions",    icon: FileWarning,   testid: "nav-purchase-requisitions",    mod: "purchase_orders", action: "view" },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/outstanding",  label: "Outstanding",  icon: TrendingDown, testid: "nav-outstanding",  mod: "receipts", financeOnly: true },
      { to: "/credit-notes", label: "Credit Notes", icon: FileMinus,    testid: "nav-credit-notes", mod: "invoices", financeOnly: true },
      { to: "/debit-notes",  label: "Debit Notes",  icon: FilePlus2,    testid: "nav-debit-notes",  mod: "invoices", financeOnly: true },
      { to: "/banks",        label: "Banks",        icon: Landmark,     testid: "nav-banks",        mod: "banks", financeOnly: true },
    ],
  },
  {
    label: "GST",
    items: [
      { to: "/gst",               label: "GST Dashboard", icon: LayoutDashboard, testid: "nav-gst-dashboard", mod: "gst" },
      { to: "/gst/gstr1",         label: "GSTR-1",        icon: FileText,        testid: "nav-gst-gstr1",     mod: "gst" },
      { to: "/gst/gstr2b",        label: "GSTR-2B",       icon: ShoppingCart,    testid: "nav-gst-gstr2b",    mod: "gst" },
      { to: "/gst/tax-liability", label: "Tax Liability", icon: Scale,           testid: "nav-gst-liability", mod: "gst" },
      { to: "/gst/itc",           label: "ITC Register",  icon: Layers,          testid: "nav-gst-itc",       mod: "gst" },
      { to: "/gst/hsn-summary",   label: "HSN Summary",   icon: Hash,            testid: "nav-gst-hsn",       mod: "gst" },
      { to: "/gst/returns",       label: "GST Returns",   icon: FileCheck2,      testid: "nav-gst-returns",   mod: "gst" },
      { to: "/gst/einvoice",      label: "E-Invoices",    icon: FileCode2,       testid: "nav-gst-einvoice",  mod: "gst" },
      { to: "/gst/eway-bill",     label: "E-Way Bills",   icon: Truck,           testid: "nav-gst-eway",      mod: "gst" },
    ],
  },
  {
    label: "Reports",
    items: [
      { to: "/reports",          label: "Reports",                  icon: BarChart3,    testid: "nav-reports",          mod: "reports", end: true },
      { to: "/reports/manufacturing-time", label: "Manufacturing Time Report", icon: Activity, testid: "nav-manufacturing-time-tracking", anyPerms: MANUFACTURING_TIME_PERMISSIONS },
      { to: "/missing-orders",   label: "Missing Expected Orders",  icon: AlertTriangle,testid: "nav-missing-orders",   mod: "reports" },
      { to: "/inactive-customers", label: "Inactive Customers",     icon: Users,        testid: "nav-inactive-customers", mod: "customers" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/users",                    label: "Users & Roles",      icon: ShieldCheck, testid: "nav-users",              mod: "users" },
      { to: "/settings/company",         label: "Company Profile",    icon: Building2,   testid: "nav-company",            mod: "users" },
       { to: "/settings/smtp",            label: "My Email (SMTP)",    icon: Mail,        testid: "nav-smtp" },
      { to: "/settings/email-templates", label: "Email Templates",    icon: Mail,        testid: "nav-email-templates", mod: "users" },
      { to: "/email-activity",           label: "Email Activity",     icon: Mail,        testid: "nav-email-activity",  mod: "users" },
      { to: "/data-import",              label: "Data Import",        icon: DatabaseZap, testid: "nav-data-import",        mod: "migration", migration: true },
      { to: "/data-health",              label: "Data Health",        icon: ShieldCheck, testid: "nav-data-health",        mod: "data_integrity" },
      { to: "/settings/business-reports",  label: "Business Reports",  icon: LineChart,   testid: "nav-business-reports",   superOnly: true },
      { to: "/settings/celebrations",      label: "Celebrations",       icon: LineChart,   testid: "nav-celebrations",        superOnly: true },
      { to: "/settings/login-announcements", label: "Login Announcements", icon: Megaphone, testid: "nav-login-announcements", superOnly: true },
      { to: "/settings/terminology",       label: "Terminology Dictionary", icon: LineChart, testid: "nav-terminology",           superOnly: true },
      { to: "/admin/reset-company-data", label: "Reset Company Data", icon: Trash2,      testid: "nav-reset-company",      superOnly: true, danger: true, hideInProduction: true },
    ],
  },
];

/* Breadcrumb inference from pathname */
export const CRUMBS = {
  "/": ["Dashboard"],
  "/chat": ["Chat"],
  // Sales & CRM
  "/customers":         ["Sales & CRM", "Customers"],
  "/customer-pos":      ["Sales & CRM", "Customer POs"],
  "/quotations":        ["Sales & CRM", "Quotations"],
  "/proformas":         ["Sales & CRM", "Proforma Invoices"],
  "/invoices":          ["Sales & CRM", "Sales Invoices"],
  "/delivery-challans": ["Sales & CRM", "Delivery Challans"],
  "/receipts":          ["Sales & CRM", "Payment Receipts"],
  // Sales & Marketing
  "/sm/dashboard": ["Sales & Marketing", "Dashboard"],
  "/sm/today":     ["Sales & Marketing", "Today"],
  "/sm/leads":     ["Sales & Marketing", "Leads"],
  "/sm/routes":    ["Sales & Marketing", "Routes"],
  // Manufacturing
  "/work-orders":      ["Manufacturing", "Work Orders"],
  "/manufacturing":    ["Manufacturing", "Production Hub"],
  "/reports/manufacturing-time": ["Reports", "Manufacturing Time Report"],
  "/production-planning": ["Manufacturing", "Production Planning"],
  "/production-board": ["Manufacturing", "Production Board"],
  "/pending-orders":   ["Manufacturing", "Pending Orders"],
  "/partial-billing":  ["Manufacturing", "Partial Billing"],
  "/production-ncs":   ["Reports", "Production NCs"],
  // Inventory
  "/templates": ["Inventory", "Product Templates"],
  "/hsn":       ["Inventory", "HSN Codes"],
  "/units":     ["Inventory", "Units"],
  // Purchase
  "/vendors":                 ["Purchase", "Vendors"],
  "/raw-materials":               ["Purchase", "Purchase Raw Materials"],
  "/store/materials":              ["Store", "Store Materials"],
  "/store/material-issues":        ["Store", "Material Issue & Return"],
  "/store/receiving":              ["Store", "Purchase Orders to Receive"],
  "/vendor-material-mappings":    ["Purchase", "Vendor Mappings"],
  "/purchase-orders":             ["Purchase", "Purchase Orders"],
   "/material-receipts":           ["Store", "Material Receipts"],
  "/purchase-requisitions":   ["Purchase", "Purchase Requisitions"],
  // Finance
  "/outstanding":  ["Finance", "Outstanding"],
  "/credit-notes": ["Finance", "Credit Notes"],
  "/debit-notes":  ["Finance", "Debit Notes"],
  "/banks":        ["Finance", "Banks"],
  // GST
  "/gst":               ["GST", "GST Dashboard"],
  "/gst/gstr1":         ["GST", "GSTR-1"],
  "/gst/gstr2b":        ["GST", "GSTR-2B"],
  "/gst/tax-liability": ["GST", "Tax Liability"],
  "/gst/itc":           ["GST", "ITC Register"],
  "/gst/hsn-summary":   ["GST", "HSN Summary"],
  "/gst/returns":       ["GST", "GST Returns"],
  "/gst/einvoice":      ["GST", "E-Invoices"],
  "/gst/eway-bill":     ["GST", "E-Way Bills"],
  "/gst/reports":       ["GST", "Reports"],
  // Reports
  "/reports": ["Reports", "Reports"],
  // Administration
  "/users":                    ["Administration", "Users & Roles"],
  "/settings/company":         ["Administration", "Company Profile"],
  "/settings/smtp":            ["Account", "My Email (SMTP)"],
  "/settings/email-templates": ["Administration", "Email Templates"],
  "/email-activity":           ["Administration", "Email Activity"],
  "/migration":                ["Administration", "Data Import"],
  "/migration-center":         ["Administration", "Data Import"],
  "/migration-engine":         ["Administration", "Data Import"],
  "/data-import":              ["Administration", "Data Import"],
  "/data-integrity":           ["Administration", "Data Health"],
  "/data-health":              ["Administration", "Data Health"],
  "/review":                   ["Administration", "Data Health", "Customer Products"],
  "/migration-review":         ["Administration", "Data Health", "Unmatched Documents"],
  "/admin/reset-company-data":  ["Administration", "Reset Company Data"],
  "/settings/business-reports": ["Administration", "Business Reports"],
  "/settings/login-announcements": ["Administration", "Login Announcements"],
  "/settings/terminology":      ["Administration", "Terminology Dictionary"],
  "/inactive-customers":        ["Reports", "Inactive Customers"],
  "/missing-orders":            ["Reports", "Missing Expected Orders"],
  "/profile":                   ["Account", "My Profile"],
  // Misc
  "/design-preview": ["Design System"],
};

export function crumbsFor(pathname) {
  if (CRUMBS[pathname]) return CRUMBS[pathname];
  if (pathname.startsWith("/store/receiving/")) return [...CRUMBS["/store/receiving"], "Receiving details"];
  // fallback: first two segments
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return ["Dashboard"];
  const first = "/" + parts[0];
  return CRUMBS[first] ? [...CRUMBS[first], "Detail"] : parts;
}

export function navigationAllowed(item, can, roleCodes = [], financeRestricted = false) {
  if (item.financeOnly && financeRestricted) return false;
  if (item.superOnly) return roleCodes.includes("super_admin");
  if (item.adminOnly && !item.mod) return can("settings", "view");
  if (item.anyPerms) return item.anyPerms.some(([mod, action]) => can(mod, action));
  if (item.anyActions) return item.anyActions.some(action => can(item.mod, action));
  return !item.mod || can(item.mod, item.action || "view");
}

export function workflowDestinationAllowed(destination, can, roleCodes = [], financeRestricted = false) {
  if (typeof destination !== "string" || !destination.startsWith("/")) return false;
  const candidates = GROUPS.flatMap(group => group.items);
  const exact = candidates.find(item => item.to === destination);
  if (exact) return navigationAllowed(exact, can, roleCodes, financeRestricted);
  const path = destination.split("?")[0];
  const item = candidates.filter(entry => entry.to !== "/" && !entry.to.includes("?") &&
    (path === entry.to || path.startsWith(`${entry.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (!item) return false;
  if (item.financeOnly && financeRestricted) return false;
  if (path.endsWith("/new") && item.mod) return can(item.mod, "create");
  return navigationAllowed(item, can, roleCodes, financeRestricted);
}
